import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, apiDelete, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = [
    "list", "form", "addBtn", "amount", "category", "account", "note",
    "frequency", "nextDate", "kindExpenseBtn", "kindIncomeBtn", "kindWrap",
    "modeRegularBtn", "modeDebtBtn", "categoryWrap", "debtWrap", "debtSelect"
  ]

  connect() {
    this.currentKind = "expense"
    this.currentMode = "regular"
    this.editingId = null
    this.load()
  }

  async load() {
    try {
      const [recData, catData, accData, debtData] = await Promise.all([
        apiGet("/api/recurring_transactions"),
        apiGet("/api/categories"),
        apiGet("/api/accounts"),
        apiGet("/api/debts").catch(() => ({ debts: [] }))
      ])

      this.recurring = recData.recurring_transactions || []
      this.categories = Array.isArray(catData) ? catData : (catData.categories || [])
      this.accounts = accData.accounts || []
      this.debts = (debtData.debts || []).filter(d => !d.settled)

      this.renderList()
      this.renderCategoryOptions()
      this.renderAccountOptions()
      this.renderDebtOptions()

      if (this.hasNextDateTarget && !this.nextDateTarget.value) {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        this.nextDateTarget.value = tomorrow.toISOString().slice(0, 10)
      }
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasListTarget ? this.listTarget : null, () => this.load()); return }
      if (e.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "recurring" } }))
      } else {
        console.error("Recurring load error:", e)
      }
    }
  }

  renderList() {
    if (!this.hasListTarget) return

    if (!this.recurring.length) {
      this.listTarget.innerHTML = `
        <div class="empty-state">
          <span class="empty-state-icon">${icon("refresh", "w-8 h-8 text-slate-300 mx-auto")}</span>
          <div class="empty-state-text">${t("recurring.no_recurring") || "No recurring transactions"}</div>
        </div>`
      return
    }

    const freqLabel = {
      daily: t("recurring.daily"),
      weekly: t("recurring.weekly"),
      monthly: t("recurring.monthly"),
      yearly: t("recurring.yearly")
    }

    const html = this.recurring.map(r => {
      const sign = r.kind === "expense" ? "-" : "+"
      const color = r.kind === "expense" ? "rgb(239,68,68)" : "rgb(16,185,129)"
      const statusColor = r.active ? "rgb(16,185,129)" : "rgb(148,163,184)"
      const statusText = r.active ? (t("recurring.active") || "Active") : (t("recurring.paused") || "Paused")

      let itemIcon, name, catBg
      if (r.debt) {
        itemIcon = icon("users", "w-5 h-5")
        name = r.debt.person_name
        catBg = "color-mix(in srgb, #6366f1 10%, transparent)"
      } else if (r.category) {
        itemIcon = r.category.icon
        name = r.category.name
        catBg = r.category.color ? `color-mix(in srgb, ${r.category.color} 10%, transparent)` : "rgb(248,250,252)"
      } else {
        itemIcon = icon("refresh", "w-5 h-5")
        name = "—"
        catBg = "rgb(248,250,252)"
      }

      const debtInfo = r.debt ? `<div class="text-xs truncate" style="color: #6366f1;">${t("recurring.mode_debt") || "Debt payment"}</div>` : ""

      return `
        <div class="card mb-2" style="padding: 12px 14px;">
          <div class="flex items-center gap-3">
            <div class="flex items-center justify-center flex-shrink-0"
                 style="width: 44px; height: 44px; border-radius: 12px; background-color: ${catBg}; font-size: 22px;">
              ${itemIcon}
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-medium text-sm truncate">${name}</div>
              <div class="text-xs truncate" style="color: rgb(148,163,184);">
                ${freqLabel[r.frequency] || r.frequency} · ${r.next_occurrence}
              </div>
              ${debtInfo}
              ${r.note ? `<div class="text-xs truncate" style="color: rgb(148,163,184);">${r.note}</div>` : ""}
            </div>
            <div class="flex-shrink-0 text-right">
              <div class="text-sm font-semibold" style="color: ${color};">
                ${sign}${formatMoney(r.amount, r.account.currency)}
              </div>
              <div class="text-xs" style="color: ${statusColor};">${statusText}</div>
            </div>
          </div>
          <div class="flex gap-2 mt-2 justify-end">
            <button data-action="click->pwa-recurring#toggleActive" data-id="${r.id}" data-active="${r.active}"
                    class="touch-active text-xs px-3 py-1.5 rounded-lg font-medium"
                    style="background-color: rgb(248,250,252); color: rgb(148,163,184);">
              ${r.active ? "||" : icon("trending-up", "w-3 h-3 inline")}
            </button>
            <button data-action="click->pwa-recurring#delete" data-id="${r.id}"
                    class="touch-active text-xs px-3 py-1.5 rounded-lg font-medium"
                    style="background-color: rgba(239, 68, 68, 0.1); color: rgb(239,68,68);">
              &times;
            </button>
          </div>
        </div>`
    }).join("")

    this.listTarget.innerHTML = `<div class="fade-in">${html}</div>`
  }

  renderCategoryOptions() {
    if (!this.hasCategoryTarget) return
    const filtered = this.categories.filter(c => c.kind === this.currentKind)
    this.categoryTarget.innerHTML = filtered.map(c =>
      `<option value="${c.id}">${c.icon} ${c.name}</option>`
    ).join("")
  }

  renderAccountOptions() {
    if (!this.hasAccountTarget) return
    this.accountTarget.innerHTML = this.accounts.map(a =>
      `<option value="${a.id}">${a.icon} ${a.name} (${a.currency})</option>`
    ).join("")
  }

  renderDebtOptions() {
    if (!this.hasDebtSelectTarget) return
    if (!this.debts || !this.debts.length) {
      this.debtSelectTarget.innerHTML = `<option value="">${t("recurring.select_debt") || "Select debt"}</option>`
      return
    }
    this.debtSelectTarget.innerHTML = this.debts.map(d => {
      const dir = d.direction === "i_owe" ? "→" : "←"
      return `<option value="${d.id}">${dir} ${d.person_name} (${formatMoney(d.remaining, d.currency)})</option>`
    }).join("")
  }

  setMode(event) {
    this.currentMode = event.currentTarget.dataset.mode
    const isDebt = this.currentMode === "debt"

    if (this.hasModeRegularBtnTarget && this.hasModeDebtBtnTarget) {
      this.modeRegularBtnTarget.style.backgroundColor = isDebt ? "rgb(248,250,252)" : "rgb(99,102,241)"
      this.modeRegularBtnTarget.style.color = isDebt ? "rgb(148,163,184)" : "white"
      this.modeDebtBtnTarget.style.backgroundColor = isDebt ? "rgb(99,102,241)" : "rgb(248,250,252)"
      this.modeDebtBtnTarget.style.color = isDebt ? "white" : "rgb(148,163,184)"
    }

    if (this.hasKindWrapTarget) this.kindWrapTarget.classList.toggle("hidden", isDebt)
    if (this.hasCategoryWrapTarget) this.categoryWrapTarget.classList.toggle("hidden", isDebt)
    if (this.hasDebtWrapTarget) this.debtWrapTarget.classList.toggle("hidden", !isDebt)
  }

  setKind(event) {
    this.currentKind = event.currentTarget.dataset.kind
    if (this.hasKindExpenseBtnTarget && this.hasKindIncomeBtnTarget) {
      const isExpense = this.currentKind === "expense"
      this.kindExpenseBtnTarget.style.backgroundColor = isExpense ? "rgb(239,68,68)" : "rgb(248,250,252)"
      this.kindExpenseBtnTarget.style.color = isExpense ? "white" : "rgb(148,163,184)"
      this.kindIncomeBtnTarget.style.backgroundColor = isExpense ? "rgb(248,250,252)" : "rgb(16,185,129)"
      this.kindIncomeBtnTarget.style.color = isExpense ? "rgb(148,163,184)" : "white"
    }
    this.renderCategoryOptions()
  }

  showForm() {
    if (this.hasFormTarget) this.formTarget.classList.remove("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.add("hidden")
  }

  cancelForm() {
    if (this.hasFormTarget) this.formTarget.classList.add("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.remove("hidden")
    this.editingId = null
    this.currentMode = "regular"
    if (this.hasKindWrapTarget) this.kindWrapTarget.classList.remove("hidden")
    if (this.hasCategoryWrapTarget) this.categoryWrapTarget.classList.remove("hidden")
    if (this.hasDebtWrapTarget) this.debtWrapTarget.classList.add("hidden")
    if (this.hasModeRegularBtnTarget && this.hasModeDebtBtnTarget) {
      this.modeRegularBtnTarget.style.backgroundColor = "rgb(99,102,241)"
      this.modeRegularBtnTarget.style.color = "white"
      this.modeDebtBtnTarget.style.backgroundColor = "rgb(248,250,252)"
      this.modeDebtBtnTarget.style.color = "rgb(148,163,184)"
    }
  }

  async save() {
    if (!networkStatus.online) { alertOffline(); return }
    const amount = parseFloat(this.amountTarget?.value)
    if (!amount || amount <= 0) {
      alert(t("recurring.invalid_amount") || "Please enter a valid amount")
      return
    }

    const isDebt = this.currentMode === "debt"

    const body = {
      recurring_transaction: {
        amount,
        account_id: parseInt(this.accountTarget?.value),
        note: this.noteTarget?.value || "",
        frequency: this.frequencyTarget?.value || "monthly",
        next_occurrence: this.nextDateTarget?.value
      }
    }

    if (isDebt) {
      const debtId = parseInt(this.debtSelectTarget?.value)
      if (!debtId) return
      body.recurring_transaction.debt_id = debtId
    } else {
      body.recurring_transaction.kind = this.currentKind
      body.recurring_transaction.category_id = parseInt(this.categoryTarget?.value)
    }

    try {
      if (this.editingId) {
        await apiPut(`/api/recurring_transactions/${this.editingId}`, body)
      } else {
        await apiPost("/api/recurring_transactions", body)
      }
      this.cancelForm()
      this.load()
    } catch (e) {
      if (e.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "recurring" } }))
      } else {
        console.error("Save recurring error:", e)
      }
    }
  }

  async toggleActive(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    const currentActive = event.currentTarget.dataset.active === "true"
    try {
      await apiPut(`/api/recurring_transactions/${id}`, {
        recurring_transaction: { active: !currentActive }
      })
      this.load()
    } catch (e) {
      console.error("Toggle error:", e)
    }
  }

  async delete(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    if (!confirm(t("recurring.delete_confirm") || "Delete?")) return

    try {
      await apiDelete(`/api/recurring_transactions/${id}`)
      this.load()
    } catch (e) {
      console.error("Delete recurring error:", e)
    }
  }
}
